import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Plus } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { pts } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const empty = { name: "", description: "", points_required: 100, active: true, redemption_limit: "" };

export default function BizRewards() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/business/rewards").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setF({ ...r, redemption_limit: r.redemption_limit ?? "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: f.name, description: f.description, points_required: Number(f.points_required), active: f.active, redemption_limit: f.redemption_limit === "" ? null : Number(f.redemption_limit) };
      if (editing) await api.put(`/business/rewards/${editing.id}`, payload);
      else await api.post("/business/rewards", payload);
      toast.success(editing ? "Reward updated" : "Reward created");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const toggle = async (r) => {
    try { await api.put(`/business/rewards/${r.id}`, { name: r.name, description: r.description, points_required: r.points_required, active: !r.active, redemption_limit: r.redemption_limit }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!list) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-3xl flex items-center gap-2"><Gift size={26} className="text-primary" /> Rewards</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-reward-btn" onClick={openNew} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"><Plus size={18} className="mr-1" /> New reward</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-black/10">
            <DialogHeader><DialogTitle className="font-display">{editing ? "Edit reward" : "New reward"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs text-muted-foreground">Name</Label><Input data-testid="reward-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
              <div><Label className="text-xs text-muted-foreground">Description</Label><Textarea data-testid="reward-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 rounded-xl" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">Points required</Label><Input data-testid="reward-points" type="number" value={f.points_required} onChange={(e) => setF({ ...f, points_required: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
                <div><Label className="text-xs text-muted-foreground">Redemption limit</Label><Input data-testid="reward-limit" type="number" value={f.redemption_limit} onChange={(e) => setF({ ...f, redemption_limit: e.target.value })} placeholder="Unlimited" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
              </div>
              <div className="flex items-center gap-2"><Switch data-testid="reward-active" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /><span className="text-sm">Active</span></div>
              <Button data-testid="save-reward" onClick={save} disabled={saving} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">{saving ? "Saving…" : "Save"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.length === 0 && <p className="text-sm text-muted-foreground">No rewards yet. Create your first one.</p>}
        {list.map((r) => (
          <div key={r.id} data-testid="reward-card" className="card-surface p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-display font-semibold">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.description}</p>
              </div>
              <span className="font-display font-bold text-primary text-lg">{pts(r.points_required)}</span>
            </div>
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-muted-foreground">Redeemed {r.redeemed_count}× {r.redemption_limit ? `/ ${r.redemption_limit}` : ""}</span>
              <div className="flex items-center gap-3">
                <button className="text-xs text-primary" onClick={() => openEdit(r)} data-testid="edit-reward">Edit</button>
                <div className="flex items-center gap-1.5"><Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid="toggle-reward" /><span className="text-xs text-muted-foreground">{r.active ? "Active" : "Off"}</span></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
